import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import {
  CARD_COLOR_PALETTE,
  CORK_TEXTURE,
  DARK_FRAME_GRADIENT,
  DARK_FRAME_SHADOW,
  DARK_PUSH_PIN_GRADIENT,
  HOME_BOARD_THEME,
  LIGHT_FRAME_GRADIENT,
  LIGHT_FRAME_SHADOW,
  LIGHT_PUSH_PIN_GRADIENT,
} from '../lib/corkBoardTheme.js';

const rand = (min, max) => Math.random() * (max - min) + min;

function pickSessionState(palette) {
  const shuffled = [...palette];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  const rotations = Array.from({ length: 4 }, () => rand(-4, 4));
  return { shuffledPalette: shuffled, rotations };
}

const CARD_CSS = `
  .sticky-card {
    transition: transform 250ms cubic-bezier(0.16, 1, 0.3, 1),
                box-shadow 250ms cubic-bezier(0.16, 1, 0.3, 1);
    text-decoration: none;
    display: block;
  }
  .sticky-card:hover {
    transform: rotate(0deg) translateY(-6px) !important;
    box-shadow: 5px 10px 22px rgba(0, 0, 0, 0.32) !important;
  }
`;

const CARD_TEMPLATES = [
  {
    staticColor: { light: '#A8D4EC', dark: '#335070' },
    emoji: '❄️',
    tag: 'D&D Campaign',
    title: 'Wexford Reach',
    description: 'A slower-burn campaign archive in the Lugsaas Chain',
    href: '/wexford',
  },
  {
    emoji: '🖋️',
    tag: 'Campaign Notes',
    title: 'Teldire',
    description: 'An active archive of notes from the world of Teldire',
    href: '/teldire/sessions',
  },
  {
    emoji: '⚔️',
    tag: 'D&D Initiative Tracker',
    title: 'Marching Order',
    description: 'A tabletop tool for keeping combat turns clear and quick',
    href: '/marching-order',
    documentNavigation: true,
  },
  {
    emoji: '🧔',
    tag: 'About Me',
    title: 'Daulton Jenkins',
    description: 'Developer, technical lead, and tinkerer',
    href: '/resume',
  },
];

function StickyCard({ color, darkColor, rotation, emoji, tag, title, description, href, documentNavigation = false }) {
  const { darkMode } = useTheme();

  const cardBg = darkMode ? darkColor : color;
  const titleCol = darkMode ? '#F5EDE2' : HOME_BOARD_THEME.titleColor;
  const descColor = darkMode ? '#E8DDD0' : HOME_BOARD_THEME.descriptionColor;
  const tagCol = darkMode ? '#F5E0A8' : HOME_BOARD_THEME.tagColor;
  const pinGradient = darkMode ? DARK_PUSH_PIN_GRADIENT : LIGHT_PUSH_PIN_GRADIENT;

  const sharedProps = {
    className: 'sticky-card',
    style: {
      background: cardBg,
      borderRadius: '3px',
      padding: '1.75rem 1.25rem 1.75rem',
      width: 'clamp(180px, 26vw, 230px)',
      boxShadow: '3px 5px 12px rgba(0, 0, 0, 0.28)',
      transform: `rotate(${rotation}deg)`,
      position: 'relative',
      color: 'inherit',
    },
  };

  const inner = (
    <>
      <div style={{
        width: '18px',
        height: '18px',
        borderRadius: '50%',
        background: pinGradient,
        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.45)',
        position: 'absolute',
        top: '-9px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2,
      }} />

      <div style={{
        fontSize: '1.75rem',
        marginBottom: '0.65rem',
        lineHeight: 1,
      }}>
        {emoji}
      </div>

      <div style={{
        fontFamily: 'var(--font-ui)',
        fontWeight: 600,
        fontVariant: 'small-caps',
        fontSize: '0.68rem',
        letterSpacing: '0.1em',
        color: tagCol,
        textTransform: 'uppercase',
        marginBottom: '0.45rem',
      }}>
        {tag}
      </div>

      <p style={{
        fontFamily: 'var(--font-display)',
        fontWeight: 700,
        fontSize: '1.05rem',
        color: titleCol,
        margin: '0 0 0.4rem',
        lineHeight: 1.25,
      }}>
        {title}
      </p>

      <p style={{
        fontFamily: 'var(--font-display)',
        fontStyle: 'italic',
        fontSize: '0.85rem',
        color: descColor,
        lineHeight: 1.45,
        margin: 0,
      }}>
        {description}
      </p>
    </>
  );

  // Marching Order is served by a Pages Function, not the React router. Use a
  // normal document navigation so Cloudflare can return the mirrored Svelte app.
  if (href.startsWith('/') && !documentNavigation) {
    return <Link to={href} {...sharedProps}>{inner}</Link>;
  }

  if (href.startsWith('/')) {
    return <a href={href} {...sharedProps}>{inner}</a>;
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...sharedProps}>
      {inner}
    </a>
  );
}

export default function Home() {
  const { darkMode } = useTheme();
  const [{ shuffledPalette, rotations }] = useState(() => pickSessionState(CARD_COLOR_PALETTE));

  let dynamicIdx = 0;
  const cards = CARD_TEMPLATES.map((t, i) => {
    const rotation = rotations[i];
    if (t.staticColor) {
      return { ...t, color: t.staticColor.light, darkColor: t.staticColor.dark, rotation };
    }
    const entry = shuffledPalette[dynamicIdx++] ?? CARD_COLOR_PALETTE[0];
    return { ...t, color: entry.light, darkColor: entry.dark, rotation };
  });

  const corkBg = darkMode ? '#4A3C30' : HOME_BOARD_THEME.corkBg;
  const corkGradient = darkMode
    ? `${CORK_TEXTURE},
        radial-gradient(ellipse at 30% 50%, rgba(90,70,45,0.40) 0%, transparent 65%),
        radial-gradient(ellipse at 75% 30%, rgba(70,50,30,0.30) 0%, transparent 55%),
        radial-gradient(ellipse at 60% 80%, rgba(80,60,35,0.25) 0%, transparent 50%)
      `
    : `${CORK_TEXTURE},
        radial-gradient(ellipse at 30% 50%, ${HOME_BOARD_THEME.corkGradients[0]} 0%, transparent 65%),
        radial-gradient(ellipse at 75% 30%, ${HOME_BOARD_THEME.corkGradients[1]} 0%, transparent 55%),
        radial-gradient(ellipse at 60% 80%, ${HOME_BOARD_THEME.corkGradients[2]} 0%, transparent 50%)
      `;
  const corkShadow = darkMode
    ? 'inset 0 2px 8px rgba(0,0,0,0.45)'
    : 'inset 0 2px 8px rgba(0,0,0,0.3)';

  const frameStyle = darkMode ? {
    background: DARK_FRAME_GRADIENT,
    borderRadius: '24px',
    padding: '10px',
    boxShadow: DARK_FRAME_SHADOW,
    maxWidth: '880px',
    width: '100%',
  } : {
    background: LIGHT_FRAME_GRADIENT,
    borderRadius: '24px',
    padding: '10px',
    boxShadow: LIGHT_FRAME_SHADOW,
    maxWidth: '880px',
    width: '100%',
  };

  return (
    <div
      className="cork-board-page"
      style={{
        // App.jsx already reserves 60px for the fixed navbar via main padding.
        // Keep this page to the remaining viewport so a fully visible corkboard
        // does not create an extra scrollbar just from layout math.
        minHeight: 'calc(100vh - 60px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: 'clamp(2rem, 7vh, 5rem) 1.5rem 3rem',
        fontFamily: 'var(--font-display)',
        color: 'var(--text-primary-warm)',
      }}
    >
      <style>{CARD_CSS}</style>

      <div style={{ width: '100%', display: 'grid', justifyItems: 'center' }}>
        <div style={{ ...frameStyle, position: 'relative' }}>
          <div style={{
            position: 'relative',
            zIndex: 1,
            backgroundColor: corkBg,
            borderRadius: '14px',
            padding: 'clamp(1.75rem, 4vw, 3rem) clamp(1.5rem, 4vw, 3rem)',
            boxShadow: corkShadow,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'clamp(2rem, 4vw, 3rem)',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <div style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '14px',
              backgroundImage: corkGradient,
              pointerEvents: 'none',
            }} />
            {cards.map((card) => (
              <StickyCard key={card.href} {...card} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
